// ui-kit: hand-built
import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
    type ComponentPropsWithRef,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
    type Ref,
} from 'react';
import {
    RiAlertFill,
    RiCheckboxCircleFill,
    RiCloseFill,
    RiForbid2Fill,
    RiInformationFill,
} from '@remixicon/react';

import { classNames } from '../../internal/classNames';

import Icon from '../Icon/Icon';
import IconAction from '../IconAction/IconAction';

export type ToastVariant = 'info' | 'warn' | 'success' | 'danger';

export type ToastProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    variant?: ToastVariant;
    onClose?: () => void;
    /** Auto-dismiss delay in seconds. `0` disables auto-dismiss. */
    timeout?: number;
    children: ReactNode;
    action?: ReactElement | null;
    ref?: Ref<HTMLDivElement>;
};

export type ToastService = {
    error: (content: ReactNode, secondsToHide?: number, config?: Partial<ToastProps>) => void;
    info: (content: ReactNode, secondsToHide?: number, config?: Partial<ToastProps>) => void;
    warn: (content: ReactNode, secondsToHide?: number, config?: Partial<ToastProps>) => void;
    success: (content: ReactNode, secondsToHide?: number, config?: Partial<ToastProps>) => void;
};

export type ToastProviderProps = {
    children: ReactNode;
};

type ToastConfig = { id: string } & ToastProps;

type ToastStore = {
    subscribe: (listener: () => void) => () => void;
    getConfigs: () => ToastConfig[];
    add: (config: ToastConfig) => void;
    remove: (id: string) => void;
};

const VARIANT_ICONS = {
    info: RiInformationFill,
    warn: RiAlertFill,
    success: RiCheckboxCircleFill,
    danger: RiForbid2Fill,
} as const;

const EXIT_MS = 600;

function createToastStore(): ToastStore {
    let configs: ToastConfig[] = [];
    const listeners = new Set<() => void>();

    const notify = () => {
        listeners.forEach((listener) => listener());
    };

    return {
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        getConfigs() {
            return configs;
        },
        add(config) {
            configs = [...configs, config];
            notify();
        },
        remove(id) {
            const next = configs.filter((t) => t.id !== id);
            if (next.length === configs.length) {
                return;
            }
            configs = next;
            notify();
        },
    };
}

function pushToast(
    store: ToastStore,
    variant: ToastVariant,
    content: ReactNode,
    config: Partial<ToastProps> = {},
) {
    const id = Math.random().toString(36).substring(2, 9);

    store.add({
        ...config,
        id,
        variant,
        onClose: () => {
            store.remove(id);
            config.onClose?.();
        },
        children: content,
        timeout: typeof config.timeout === 'number' ? config.timeout : 10,
        action: config.action ?? null,
    });
}

export const ToastContext = createContext<ToastService | null>(null);

export function ToastProvider({ children }: ToastProviderProps) {
    const [store] = useState(createToastStore);

    const toastService = useMemo<ToastService>(
        () => ({
            error(content, secondsToHide = 30, config) {
                pushToast(store, 'danger', content, {
                    ...config,
                    timeout: secondsToHide > 0 ? secondsToHide : 0,
                });
            },
            info(content, secondsToHide = 10, config) {
                pushToast(store, 'info', content, {
                    ...config,
                    timeout: secondsToHide > 0 ? secondsToHide : 0,
                });
            },
            warn(content, secondsToHide = 10, config) {
                pushToast(store, 'warn', content, {
                    ...config,
                    timeout: secondsToHide > 0 ? secondsToHide : 0,
                });
            },
            success(content, secondsToHide = 10, config) {
                pushToast(store, 'success', content, {
                    ...config,
                    timeout: secondsToHide > 0 ? secondsToHide : 0,
                });
            },
        }),
        [store],
    );

    return (
        <ToastContext.Provider value={toastService}>
            {children}
            <ToastList store={store} />
        </ToastContext.Provider>
    );
}

function ToastList({ store }: { store: ToastStore }) {
    const configs = useSyncExternalStore(store.subscribe, store.getConfigs, store.getConfigs);

    return (
        <div
            className='ui-toast-container'
            data-test-component='ui/ToastContainer'>
            {configs.map(({ id, ...props }) => (
                <Toast
                    key={id}
                    {...props}
                />
            ))}
        </div>
    );
}

function Toast({
    variant = 'info',
    onClose,
    timeout = 0,
    children,
    action = null,
    className = '',
    ref,
    ...rest
}: ToastProps) {
    const [exiting, setExiting] = useState(false);
    const exitingRef = useRef(false);
    const onCloseRef = useRef(onClose);
    const timeoutRef = useRef(timeout);
    onCloseRef.current = onClose;
    timeoutRef.current = timeout;

    const beginExit = () => {
        if (exitingRef.current) {
            return;
        }
        exitingRef.current = true;
        setExiting(true);
        window.setTimeout(() => {
            onCloseRef.current?.();
        }, EXIT_MS);
    };

    const handleClose = (e?: MouseEvent<HTMLElement>) => {
        e?.stopPropagation();
        e?.preventDefault();
        beginExit();
    };

    useEffect(() => {
        const seconds = timeoutRef.current;
        if (seconds <= 0) {
            return;
        }

        const tId = window.setTimeout(() => {
            if (exitingRef.current) {
                return;
            }
            exitingRef.current = true;
            setExiting(true);
            window.setTimeout(() => {
                onCloseRef.current?.();
            }, EXIT_MS);
        }, seconds * 1000);

        return () => {
            window.clearTimeout(tId);
        };
    }, []);

    return (
        <div
            {...rest}
            ref={ref}
            className={classNames({
                'ui-toast': true,
                [`ui-toast--${variant}`]: true,
                'ui-toast--with-transition': true,
                'ui-toast--exiting': exiting,
                [className]: Boolean(className),
            })}
            data-test-component='ui/Toast'
            role='status'>
            <div className='ui-toast__wrapper'>
                <Icon
                    icon={VARIANT_ICONS[variant]}
                    className='ui-toast__icon'
                    size={16}
                />
                <div
                    className='ui-toast__content'
                    data-test-name='content'>
                    {children}
                </div>
                {action ? (
                    <div
                        className='ui-toast__action'
                        data-test-name='action'>
                        {action}
                    </div>
                ) : null}
                <IconAction
                    icon={RiCloseFill}
                    onClick={handleClose}
                    aria-label='Dismiss'
                    data-test-name='close'
                    className='ui-toast__close'
                />
            </div>
        </div>
    );
}

Toast.displayName = 'Toast';

export function useToast(): ToastService {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return ctx;
}

/** Alias matching Platform's hook name for a frictionless swap. */
export const useToasts = useToast;

export default Toast;
